
import React, { useState } from 'react';
import type { AdminUser, Department, AvailabilityStatus, DayOfWeek, AppPage, ReportPermissionLevel, ReportId } from '../../types';
import { ALL_PAGES, REPORT_CONFIG } from '../../types';
import Modal from '../ui/Modal';
import { Icons } from '../ui/Icons';
import AvailabilityCalendar from '../staff/AvailabilityCalendar';
import { getFirebaseAuth } from '../../services/firebase';
import { toast } from 'react-hot-toast';

interface EditAdminModalProps {
    admin: AdminUser;
    departments: Department[];
    weekStartDay: DayOfWeek;
    onClose: () => void;
    onSave: (admin: AdminUser) => void;
}

const EditAdminModal: React.FC<EditAdminModalProps> = ({ admin, departments, weekStartDay, onClose, onSave }) => {
    const [activeTab, setActiveTab] = useState<'profile' | 'availability' | 'settings' | 'permissions' | 'security' | 'reports'>('profile');
    const [formData, setFormData] = useState<AdminUser>(admin);
    const [newPassword, setNewPassword] = useState('');
    const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

     const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        if (type === 'checkbox') {
            const { checked } = e.target as HTMLInputElement;
            setFormData({ ...formData, [name]: checked });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleOverridesChange = (overrides: { [date: string]: AvailabilityStatus; }) => {
        setFormData(prev => ({ ...prev, availabilityOverrides: overrides }));
    };

     const handleDefaultAvailabilityChange = (day: DayOfWeek, status: AvailabilityStatus) => {
        setFormData(prev => ({
            ...prev,
            availability: { ...prev.availability, [day]: status }
        }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave(formData);
        onClose();
    };

    const renderProfileTab = () => (
         <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Full Name</label>
                    <input type="text" name="name" id="name" value={formData.name} onChange={handleChange} required className="mt-1 block w-full border border-gray-300 dark:border-slate-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-200" />
                </div>
                 <div>
                    <label htmlFor="role" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Role</label>
                    <input type="text" name="role" id="role" value={formData.role} onChange={handleChange} required className="mt-1 block w-full border border-gray-300 dark:border-slate-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-200" />
                </div>
            </div>
            <div>
                 <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Phone Number</label>
                <input type="tel" name="phone" id="phone" value={formData.phone} onChange={handleChange} required className="mt-1 block w-full border border-gray-300 dark:border-slate-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-200" />
            </div>
            <div>
                <label htmlFor="departmentId" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Department</label>
                <select 
                    name="departmentId" 
                    id="departmentId" 
                    value={formData.departmentIds?.[0] || ''} 
                    onChange={(e) => {
                        const val = e.target.value;
                        setFormData(prev => ({
                            ...prev,
                            departmentIds: val ? [val] : []
                        }));
                    }} 
                    className="mt-1 block w-full border border-gray-300 dark:border-slate-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-200"
                >
                    <option value="">Select a department</option>
                    {departments.map(dep => <option key={dep.id} value={dep.id}>{dep.name}</option>)}
                </select>
            </div>
            <div className="flex items-center gap-2 py-2">
                <input
                    type="checkbox"
                    id="isDriver"
                    name="isDriver"
                    checked={formData.isDriver || false}
                    onChange={handleChange}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="isDriver" className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <Icons.Bus className="w-4 h-4 text-indigo-500" />
                    This admin is a Driver
                </label>
            </div>
            <div>
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Notes</label>
                <textarea name="notes" id="notes" value={formData.notes} onChange={handleChange} rows={3} className="mt-1 block w-full border border-gray-300 dark:border-slate-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-200"></textarea>
            </div>
        </div>
    );
    
     const renderAvailabilityTab = () => (
         // FIX: The AvailabilityCalendar component was called without the required 'variant' prop. It's now split into two calls, one for 'overrides' and one for 'defaults', to provide full editing capability within the tab.
         <div>
             <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-2">Date-Specific Overrides</h4>
             <AvailabilityCalendar
                availability={formData.availability}
                availabilityOverrides={formData.availabilityOverrides || {}}
                weekStartDay={weekStartDay}
                onOverridesChange={handleOverridesChange}
                onAvailabilityChange={handleDefaultAvailabilityChange}
                variant="overrides"
            />
            <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mt-6 mb-2">Default Weekly Schedule</h4>
            <AvailabilityCalendar
                availability={formData.availability}
                availabilityOverrides={formData.availabilityOverrides || {}}
                weekStartDay={weekStartDay}
                onOverridesChange={handleOverridesChange}
                onAvailabilityChange={handleDefaultAvailabilityChange}
                variant="defaults"
            />
         </div>
    );
    
    const renderSettingsTab = () => (
        <div>
            <div className="flex items-start">
                <div className="flex items-center h-5">
                    <input id="showInScheduler" name="showInScheduler" type="checkbox" checked={formData.showInScheduler} onChange={handleChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 dark:border-slate-500 rounded bg-gray-100 dark:bg-slate-600" />
                </div>
                <div className="ml-3 text-sm">
                    <label htmlFor="showInScheduler" className="font-medium text-gray-700 dark:text-gray-300">Show in Scheduler</label>
                    <p className="text-gray-500 dark:text-gray-400">Enable this to allow this admin to be assigned shifts.</p>
                </div>
            </div>
             <div className="mt-4">
                <label htmlFor="color" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Display Color</label>
                <input type="color" name="color" id="color" value={formData.color} onChange={handleChange} className="mt-1 block w-full h-10 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm" />
            </div>
        </div>
    );

    const handleTogglePage = (page: AppPage) => {
        const currentAllowed = formData.allowedPages || ALL_PAGES;
        const newAllowed = currentAllowed.includes(page)
            ? currentAllowed.filter(p => p !== page)
            : [...currentAllowed, page];
        setFormData({ ...formData, allowedPages: newAllowed });
    };

    const handleReportPermissionChange = (reportId: string, level: ReportPermissionLevel) => {
        const currentPerms = formData.reportPermissions || {};
        setFormData({
            ...formData,
            reportPermissions: {
                ...currentPerms,
                [reportId]: level
            }
        });
    };

    const handleSelectAllReports = (level: ReportPermissionLevel) => {
        const newPerms: { [reportId: string]: ReportPermissionLevel } = {};
        REPORT_CONFIG.forEach(report => {
            newPerms[report.id] = level;
        });
        setFormData({ ...formData, reportPermissions: newPerms });
    };

    const renderReportsTab = () => {
        const levels: { value: ReportPermissionLevel; label: string; icon: React.ReactNode }[] = [
            { value: 'none', label: 'No Access', icon: <Icons.Lock className="w-3.5 h-3.5" /> },
            { value: 'view', label: 'View Only', icon: <Icons.Eye className="w-3.5 h-3.5" /> },
            { value: 'generate', label: 'Generate', icon: <Icons.Play className="w-3.5 h-3.5" /> },
            { value: 'export', label: 'Export', icon: <Icons.Download className="w-3.5 h-3.5" /> },
        ];

        return (
            <div className="space-y-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-indigo-50/50 dark:bg-indigo-900/10 p-4 rounded-xl border border-indigo-100/50 dark:border-indigo-900/20">
                    <div>
                        <h4 className="text-sm font-bold text-indigo-900 dark:text-indigo-300">Quick Actions</h4>
                        <p className="text-xs text-indigo-700 dark:text-indigo-400/80">Apply permissions to all reports at once.</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {levels.map(level => (
                            <button
                                key={level.value}
                                type="button"
                                onClick={() => handleSelectAllReports(level.value)}
                                className="px-3 py-1.5 bg-white dark:bg-slate-800 border border-indigo-100 dark:border-indigo-900/30 text-[10px] font-black uppercase tracking-widest text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/40 transition-all shadow-sm"
                            >
                                All: {level.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-3">
                    {REPORT_CONFIG.map(report => {
                        const currentLevel = formData.reportPermissions?.[report.id] || 'none';
                        return (
                            <div key={report.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-white dark:bg-slate-900 border border-gray-100 dark:border-slate-800 rounded-2xl hover:border-indigo-200 dark:hover:border-indigo-900 transition-all group">
                                <div className="flex items-center gap-3 mb-3 sm:mb-0">
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                                        currentLevel !== 'none' 
                                            ? 'bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-400' 
                                            : 'bg-gray-100 dark:bg-slate-800 text-gray-400 dark:text-gray-600'
                                    }`}>
                                        <Icons.FileText className="w-4 h-4" />
                                    </div>
                                    <span className="text-sm font-bold text-gray-900 dark:text-white">{report.label}</span>
                                </div>
                                
                                <div className="flex bg-gray-100 dark:bg-slate-800 p-1 rounded-xl w-fit">
                                    {levels.map(level => (
                                        <button
                                            key={level.value}
                                            type="button"
                                            onClick={() => handleReportPermissionChange(report.id, level.value)}
                                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${
                                                currentLevel === level.value
                                                    ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm'
                                                    : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'
                                            }`}
                                            title={level.label}
                                        >
                                            {level.icon}
                                            <span className="hidden lg:inline">{level.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const renderPermissionsTab = () => {
        const pageLabels: { [key in AppPage]: string } = {
            scheduler: 'Scheduler (Full Access)',
            schedulerPreview: 'Schedule (Read-Only)',
            staffList: 'Staff Management',
            settings: 'Reception Settings',
            myProfile: 'My Profile',
            timeOff: 'Time Off Requests',
            workforce: 'Workforce Planning',
            shuttle: 'Shuttle Management',
            parking: 'Parking Management',
            reports: 'Reports & Analytics',
            attendance: 'Attendance Management',
            taskManager: 'Task Manager',
            memos: 'Memos & Announcements',
            maintenance: 'Maintenance Management',
            help: 'Help & Documentation',
            accessManager: 'Access Manager'
        };

        return (
            <div className="space-y-4">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    Select the pages this administrator is allowed to access. Unselected pages will be hidden from their navigation.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {ALL_PAGES.map(page => (
                        <label key={page} className="flex items-center p-3 rounded-lg border border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors">
                            <input
                                type="checkbox"
                                checked={(formData.allowedPages || ALL_PAGES).includes(page)}
                                onChange={() => handleTogglePage(page)}
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            />
                            <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-200">
                                {pageLabels[page] || page}
                            </span>
                        </label>
                    ))}
                </div>
            </div>
        );
    };

    const handleUpdatePassword = async () => {
        if (!newPassword || newPassword.length < 6) {
            toast.error('Password must be at least 6 characters long.');
            return;
        }

        setIsUpdatingPassword(true);
        try {
            const adminToken = await getFirebaseAuth().currentUser?.getIdToken();
            if (!adminToken) throw new Error('Not authenticated');

            const response = await fetch('/api/admin/update-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    uid: admin.uid,
                    newPassword,
                    adminToken
                })
            });

            const result = await response.json();
            if (!response.ok) throw new Error(result.error || 'Failed to update password');

            toast.success('Password updated successfully!');
            setNewPassword('');
        } catch (error: any) {
            console.error('Password update error:', error);
            toast.error(error.message || 'An unexpected error occurred.');
        } finally {
            setIsUpdatingPassword(false);
        }
    };

    const renderSecurityTab = () => (
        <div className="space-y-6">
            <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/30 p-4 rounded-lg">
                <div className="flex gap-3">
                    <Icons.AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0" />
                    <div>
                        <h4 className="text-sm font-bold text-amber-900 dark:text-amber-300">Security Notice</h4>
                        <p className="text-xs text-amber-700 dark:text-amber-400/80 mt-1">
                            Setting a new password will immediately update the administrator's login credentials. 
                            Ensure you communicate the new password to them securely.
                        </p>
                    </div>
                </div>
            </div>

            <div className="space-y-4">
                <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        New Password
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Icons.Lock className="h-4 w-4 text-gray-400" />
                        </div>
                        <input
                            type="password"
                            id="newPassword"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="block w-full pl-10 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-200"
                            placeholder="Enter new password"
                        />
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Minimum 6 characters.</p>
                </div>

                <button
                    type="button"
                    onClick={handleUpdatePassword}
                    disabled={isUpdatingPassword || !newPassword}
                    className="w-full flex justify-center items-center gap-2 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-300 transition-colors"
                >
                    {isUpdatingPassword ? (
                        <>
                            <Icons.Loader2 className="w-4 h-4 animate-spin" />
                            Updating...
                        </>
                    ) : (
                        <>
                            <Icons.Key className="w-4 h-4" />
                            Update Password
                        </>
                    )}
                </button>
            </div>
        </div>
    );

    const tabs = [
        { id: 'profile', label: 'Profile', icon: <Icons.User className="w-5 h-5"/> },
        { id: 'availability', label: 'Availability', icon: <Icons.CalendarCheck className="w-5 h-5"/> },
        { id: 'settings', label: 'Scheduling', icon: <Icons.Settings className="w-5 h-5"/> },
        { id: 'permissions', label: 'Permissions', icon: <Icons.Shield className="w-5 h-5"/> },
        { id: 'reports', label: 'Reports', icon: <Icons.FileText className="w-5 h-5"/> },
        { id: 'security', label: 'Security', icon: <Icons.Lock className="w-5 h-5"/> },
    ];

    return (
        <Modal title={`Edit Admin: ${admin.name}`} onClose={onClose}>
            <form onSubmit={handleSubmit}>
                 <div className="p-6">
                    <div className="border-b border-gray-200 dark:border-slate-700 mb-6">
                        <nav className="-mb-px flex space-x-6" aria-label="Tabs">
                            {tabs.map(tab => (
                                <button
                                    type="button" key={tab.id} onClick={() => setActiveTab(tab.id as any)}
                                    className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                                        activeTab === tab.id
                                            ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                                            : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-slate-600'
                                    }`}>
                                    {tab.icon} {tab.label}
                                </button>
                            ))}
                        </nav>
                    </div>
                    {activeTab === 'profile' && renderProfileTab()}
                    {activeTab === 'availability' && renderAvailabilityTab()}
                    {activeTab === 'settings' && renderSettingsTab()}
                    {activeTab === 'permissions' && renderPermissionsTab()}
                    {activeTab === 'reports' && renderReportsTab()}
                    {activeTab === 'security' && renderSecurityTab()}
                </div>
                <div className="bg-gray-50 dark:bg-slate-900/50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t dark:border-slate-700">
                    <button type="submit" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-800 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm">
                        Save Changes
                    </button>
                    <button type="button" onClick={onClose} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-slate-600 shadow-sm px-4 py-2 bg-white dark:bg-slate-700 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-800 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">
                        Cancel
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default EditAdminModal;